import { type ChangeEvent, memo, useCallback, useEffect, useState } from 'react';
import { useToggle } from 'react-use';

import { ToggleField } from '@air/component-toggle-field';
import { EyeClosed as EyeClosedIcon, Eye as EyeIcon } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { Input } from '@air/primitive-input';

import { UpsellFlashIconButton } from '~/components/SharePrivateClipModalV2/UpsellFlashIconButton';
import { useSharePrivateCollectionModal } from '~/components/SharePrivateCollectionModal/SharePrivateCollectionModalContext';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useShareSettings } from '~/hooks/useShareSettings';
import { PASSWORD_REQS_STRING } from '~/utils/Auth';

export const PasscodeProtectionSetting = memo(() => {
  const { shortUrlProps, updatePasscode, savePasscode, isEditMode, errors, isSaving } =
    useSharePrivateCollectionModal();
  const { passcodeEnabled } = useShareSettings();
  const [showPasscode, toggleShowPasscode] = useToggle(false);
  const [isShowingPasscodeForm, showPasscodeForm] = useToggle(!!shortUrlProps.passcode);

  const [passcode, setPasscode] = useState<string | null>(shortUrlProps.passcode ?? null);

  // Sync local state with context changes (e.g., after save)
  useEffect(() => {
    setPasscode(shortUrlProps.passcode ?? null);
    showPasscodeForm(!!shortUrlProps.passcode);
  }, [shortUrlProps.passcode, showPasscodeForm]);

  const handleCheckedChange = useCallback(
    (checked: boolean) => {
      if (isShowingPasscodeForm) {
        // Check if there was a previous value BEFORE updating state
        const hadPasscode = shortUrlProps.passcode !== null;
        setPasscode(null);
        updatePasscode(null);
        // In edit mode, only save if there was a previous value (avoid no-op)
        // Pass explicit null to avoid race condition with state update
        if (isEditMode && hadPasscode) {
          savePasscode?.(null);
        }
      }
      showPasscodeForm(checked);
    },
    [isShowingPasscodeForm, updatePasscode, showPasscodeForm, isEditMode, savePasscode, shortUrlProps.passcode],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newPasscode = e.target.value;
      setPasscode(newPasscode);
      // Always update parent state immediately for validation feedback
      updatePasscode(newPasscode);
    },
    [updatePasscode],
  );

  const handleSave = useCallback(() => {
    savePasscode?.();
  }, [savePasscode]);

  return (
    <div className="py-2">
      <ToggleField
        label="Add passcode protection"
        id="password-protect"
        checked={isShowingPasscodeForm}
        dataTestId="PASSCODE_PROTECTION_LABEL"
        labelTestId="PASSCODE_PROTECTION_TOGGLE"
        isLoading={isSaving}
        disabled={!passcodeEnabled || isSaving}
        onCheckedChange={handleCheckedChange}
      >
        {!passcodeEnabled && <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>}
      </ToggleField>

      {passcodeEnabled && isShowingPasscodeForm && (
        <>
          <div className="relative mt-3 flex items-center">
            <div className="relative w-full">
              <Input
                size="large"
                value={passcode ?? ''}
                data-testid="PASSCODE_PROTECTION_INPUT"
                onChange={handleChange}
                type={showPasscode ? 'text' : 'password'}
                name="passcode"
                className="w-full rounded-8 pr-8"
                placeholder="Enter passcode"
                onBlur={handleChange}
                autoComplete="new-password"
              />
              <IconButton
                appearance="ghost"
                className="absolute top-1 right-1"
                color="grey"
                label="Show passcode"
                icon={showPasscode ? EyeIcon : EyeClosedIcon}
                onClick={toggleShowPasscode}
                size="medium"
                type="button"
              />
            </div>
            {isEditMode && (
              <Button
                appearance="filled"
                className="ml-3"
                color="grey"
                data-testid="PASSCODE_PROTECTION_SUBMIT_BUTTON"
                disabled={!passcode || !!errors?.passcode}
                isLoading={isSaving}
                size="large"
                onClick={handleSave}
                type="button"
              >
                Save
              </Button>
            )}
          </div>
          {errors?.passcode && <p className="text-12 text-red-9">{errors.passcode}</p>}
          {!errors?.passcode && <p className="text-12 text-grey-10">{PASSWORD_REQS_STRING}</p>}
        </>
      )}
    </div>
  );
});

PasscodeProtectionSetting.displayName = 'PasscodeProtectionSetting';
